import React, { useState } from "react";
import PropTypes from "prop-types";
import { Alert, AlertTitle, IconButton, Collapse, Paper } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { classifyError } from "../../utils/classifyError.js";
import { getGenericErrorCopy } from "../../utils/genericErrorCopy.js";

const ErrorBox = React.forwardRef((props, ref) => {
  const [open, setOpen] = useState(false);

  function getAlertAction() {
    if (!props.error) return;

    return (
      <IconButton size="large" onClick={() => setOpen(!open)}>
        {open && <ExpandLessIcon />}
        {!open && <ExpandMoreIcon />}
      </IconButton>
    );
  }

  function getErrorDetail() {
    if (!props.error) return;

    const { message, code, params, response } = props.error;
    return (
      <Collapse in={open}>
        <Paper sx={{ mt: 4, p: 2 }}>
          {props.error.message}
          <pre style={{ width: "100%", maxWidth: "75vw" }}>
            {JSON.stringify({ message, code, params, response: response?.data }, null, 2)}
          </pre>
          <pre style={{ width: "100%", maxWidth: "75vw" }}>{props.error.trace || props.error.stack}</pre>
        </Paper>
      </Collapse>
    );
  }

  function getGenericCopy() {
    return getGenericErrorCopy(classifyError(props.error), { surface: "alert" });
  }

  function getErrorTitle() {
    if (props.userError) {
      return <AlertTitle>{props.userError.title}</AlertTitle>;
    }
    return <AlertTitle>{getGenericCopy().title}</AlertTitle>;
  }

  function getErrorMessage() {
    if (props.userError) {
      return props.userError.message;
    }
    return getGenericCopy().message;
  }

  return (
    <Alert ref={ref} sx={{ width: "100%", ...props.sx }} severity={"error"} action={getAlertAction()}>
      {getErrorTitle()}
      {getErrorMessage()}
      {getErrorDetail()}
    </Alert>
  );
});

ErrorBox.propTypes = {
  error: PropTypes.shape({
    message: PropTypes.node.isRequired,
    code: PropTypes.string,
    params: PropTypes.object,
    response: PropTypes.object,
    trace: PropTypes.node,
    stack: PropTypes.node,
  }),

  userError: PropTypes.shape({
    title: PropTypes.node,
    message: PropTypes.node.isRequired,
  }),

  sx: PropTypes.object,
};

ErrorBox.displayName = "ErrorBox";

export default ErrorBox;
